import { Modal, useLocalStorageAsState, useScreenWidth } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { FullScreenToggleIconButton } from "../../../commons/button/FullScreenToggleIconButton.js";
import {
  ExpressionInputWithVariables,
  type PropertyOption,
  type VariableInputState,
} from "./ExpressionInputWithVariables.js";

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  expression: string | undefined;
  expressionError?: Error | null;
  handleClose: () => void;
  handleSave: (
    expression: string,
    handleClose: () => void
  ) => Promise<void> | void;
  handleDelete: (handleClose: () => void) => Promise<void> | void;
  filterOptions?: (option: PropertyOption) => boolean;
  isFullScreenLocalStorageKey?: string;
}

export function ExpressionModal({
  projectId,
  docId,
  itemId,
  expression: passedExpression,
  expressionError,
  handleClose,
  handleSave,
  handleDelete,
  filterOptions,
  isFullScreenLocalStorageKey,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const { isMaxPhone } = useScreenWidth();
  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    `msys-expression-modal-fullscreen-${isFullScreenLocalStorageKey ?? "common"}`,
    false,
    true
  );

  const [inputState, setInputState] = React.useState<VariableInputState>({
    expression: passedExpression ?? "",
    cursorIndex: null,
  });

  return (
    <Modal
      title={t("Build your formula", { ns: "QuoteItem" })}
      dialogProps={
        !isMaxPhone && isFullScreen ? { fullScreen: true } : undefined
      }
      headerActions={
        !isMaxPhone ? (
          <FullScreenToggleIconButton
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
          />
        ) : undefined
      }
      alwaysVisible
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: {
            variant: "text",
          },
        },
        {
          label: t("Delete", { ns: "Global" }),
          handleClick: () => handleDelete(handleClose),
          buttonProps: {
            color: "secondary",
            disabled: !passedExpression,
          },
        },
        {
          label: t("Save", { ns: "Global" }),
          handleClick: async () => {
            await handleSave(inputState.expression, handleClose);
          },
          buttonProps: {
            disabled: !inputState.expression.trim(),
          },
        },
      ]}
      handleClose={handleClose}
    >
      <ExpressionInputWithVariables
        label={t("Formula", { ns: "QuoteItem" })}
        itemId={itemId}
        docId={docId}
        projectId={projectId}
        expressionError={expressionError}
        inputState={inputState}
        onInputStateChange={setInputState}
        filterOptions={filterOptions}
        autoFocus={true}
        direction="column"
        showSelectedVariableAllowedValues={true}
      />
    </Modal>
  );
}
